import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { parseSize } from "@/zapi-frontend/shared/lib/parseSize";
import { splitFileName, removeTimestampPrefix } from "@/zapi-frontend/shared/lib/splitFileName";
import { AppButton, Typography } from "@libs/zapi-ui/custom";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from "@libs/zapi-ui/shadcn";
import { DotsThreeIcon, GlobeIcon, TrashSimpleIcon } from "@phosphor-icons/react";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { useMemo } from "react";
import { TMinFile } from "../model/types";

export function RepositoryFile({
  file,
  onDelete,
  disabled = false
}: {
  file: Pick<TMinFile, "name" | "size" | "webUrl"> | Pick<File, "name" | "size">;
  onDelete: () => void;
  disabled?: boolean;
}) {
  const webUrl = "webUrl" in file ? file.webUrl : undefined;
  const isWebLink = !!webUrl;

  const filenameWithoutTimestamp = removeTimestampPrefix(file.name);
  const { name: fileName, extension: fileType } = splitFileName(filenameWithoutTimestamp);
  const { firstName, lastName } = useMeStore();

  const sizeField = useMemo(() => {
    if (!file.size) return "";
    const { value, unit } = parseSize(file.size);
    return `${value}${unit}`;
  }, [file.size]);

  return (
    <div className="flex items-center justify-between py-3 bg-white border-b border-innoveraZinc-150 transition-colors group">
      <div className="flex items-end gap-1 min-w-0">
        <div className="w-[28px] h-[36px] pb-1 flex justify-center items-center rounded-[5px] border border-innoveraZinc-150">
          {isWebLink ? (
            <GlobeIcon className="w-4 h-4 text-innoveraZinc-500" />
          ) : (
            <Typography color="zinc500" className="text-[8px] uppercase">
              {fileType}
            </Typography>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <Typography variant="label2" className="truncate leading-4">
            {isWebLink ? webUrl : fileName}
          </Typography>
          <Typography variant="body3" color="zinc500">
            {isWebLink ? "" : sizeField + " "}Uploaded by {firstName} {lastName}
          </Typography>
        </div>
      </div>

      <div className="flex-shrink-0 ml-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild data-testid="project-header-dropdown-trigger" disabled={disabled}>
            <AppButton
              title="Project Actions"
              appearance="text"
              color="zinc100"
              size="icon"
              loadingText="Deleting..."
              endAdornment={<DotsThreeIcon weight="bold" className="text-innoveraZinc-900" />}
              disabled={disabled}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="p-1 border-innoveraZinc-100">
            <DropdownMenuItem
              onClick={onDelete}
              data-testid="project-header-dropdown-delete"
              className="px-3 py-2 flex items-center gap-1"
              disabled={disabled}
            >
              <TrashSimpleIcon className="text-innoveraZinc-900" />
              <Typography variant="label2">Delete</Typography>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
